/**
 * Report bundles: a coded report packaged as source files and compiled by the
 * server, so it can be written and deployed without a release of this app.
 *
 * A bundle is a small TypeScript project with two entry points. `definition.ts`
 * exports `definition`, a ReportDefinition — pure code that runs both in the
 * browser and in the server sandbox. `document.tsx` exports `Document` and
 * `Inputs`, the React components, and runs only in the browser. Both may
 * import each other's neighbours, `react`, and `@veriform/report-sdk`; nothing
 * else exists inside a bundle.
 *
 * Compilation resolves those two imports to the host global the app installs
 * (host.ts in the browser, the sandbox prelude on the server), which is how one
 * compiled artifact runs in both places against the app's own React and SDK.
 */

export const REPORT_BUNDLE_DEFINITION_ENTRY = 'definition.ts';
export const REPORT_BUNDLE_DOCUMENT_ENTRY = 'document.tsx';

export const REPORT_SDK_MODULE = '@veriform/report-sdk';
/** the modules a bundle may import from outside itself */
export const REPORT_HOST_MODULES: readonly string[] = ['react', 'react/jsx-runtime', REPORT_SDK_MODULE];
/** `globalThis[REPORT_HOST_GLOBAL].require(name)` hands a compiled bundle its host modules */
export const REPORT_HOST_GLOBAL = '__veriformReportHost';
/** the compiled bundle's exports land on this variable (esbuild `globalName`) */
export const REPORT_BUNDLE_GLOBAL = '__veriformReportBundle';
/** the pure SDK, as an IIFE the server sandbox evaluates first */
export const REPORT_SDK_RUNTIME_GLOBAL = '__veriformReportSdk';

export const REPORT_BUNDLE_MAX_FILES = 64;
export const REPORT_BUNDLE_MAX_TOTAL_BYTES = 2 * 1024 * 1024;
export const REPORT_BUNDLE_FILE_EXTENSIONS = ['.ts', '.tsx', '.json'];

/** path → source. Paths are relative, `/`-separated, without `.` or `..` segments. */
export type ReportBundleFiles = Record<string, string>;

export interface ReportBundleDiagnostic {
  file: string | null;
  line: number | null;
  column: number | null;
  message: string;
}

/** What compiling a bundle produces, stored whole on every published version. */
export interface CompiledReportBundle {
  contractVersion: string;
  key: string;
  title: string;
  description: string;
  /** `definition.version`, the report's own version string */
  definitionVersion: string;
  /** what the report is about: always `run` */
  subject?: ReportSubject;
  /** the report asks for point files (`definition.traces`) */
  hasTraces?: boolean;
  /** the definition has `judge`, the verdict of record, beside or instead of `evaluate` */
  hasJudge?: boolean;
  /** the definition has settings (`defaultSettings`, `normalizeSettings`) */
  hasSettings?: boolean;
  /** what the definition declares about itself as data, for the server's sandboxed stand-in */
  meta?: CompiledDefinitionMeta;
  /** the input sheet of a report filled in from inputs: its schema's, as compiled against */
  sheet?: InputSheet;
  /** the schema `schema.json` names, and the version compiled against (null: its draft) */
  schema?: CompiledSchemaRef;
  /** each fixture in `fixtures/` evaluated at compile time */
  fixtureResults?: FixtureResult[];
  definitionJs: string;
  documentJs: string;
  /** sha256 over both scripts */
  hash: string;
}

export interface CompiledSchemaRef {
  id: string;
  key: string;
  /** null when compiled against the schema's draft */
  versionId: string | null;
  version: number | null;
}

export interface CompiledDefinitionMeta {
  requires?: Record<string, Requirement>;
  signoffs?: SignoffSpec[];
}

/**
 * A fixture: a context the report is evaluated against every time the bundle
 * compiles, with the values and settings to use and, optionally, what it
 * must come to. A path under `fixtures/` ending in `.json`. For a run report:
 *
 *   { "values": {…}, "settings": {…}, "context": { "run": {…}, … }, "expect": { "verdictLabel": "PASS" } }
 *
 * For a report filled in from its input sheet, the inputs as entered (and
 * any traces its waveforms stand for, by trace key):
 *
 *   { "inputs": {…}, "values": {…}, "traces"?: {…}, "expect": { "verdict": "passed" } }
 *
 * An expectation that isn't met leaves the draft compiling and previewable but
 * holds the bundle back from publishing, like a failing test.
 */
export interface FixtureResult {
  file: string;
  verdict: string | null;
  verdictLabel: string | null;
  findings: number;
  /** the expectation it was held to; null when it states none */
  expected: { verdict?: string; verdictLabel?: string } | null;
  ok: boolean;
  /** why it could not be evaluated, or how it missed its expectation */
  error: string | null;
  /** the first few findings that did not pass, which say why it came to its verdict; absent on results compiled before */
  reasons?: string[];
  /**
   * its checks (its own, and the rules files' that apply) as far as they were
   * made here: on the server, those on findings, and any it could not read.
   * The studio adds those on the printed document. Absent on results compiled before
   */
  checks?: CheckResult[];
  /** how many of its checks are on the printed document, made where it is drawn rather than in the compile */
  printed?: number;
}

export const REPORT_BUNDLE_FIXTURE_DIR = 'fixtures/';

/** The runtime the browser fetches to render a bundled report. */
export interface ReportBundleRuntime {
  bundleVersionId: string;
  key: string;
  contractVersion: string;
  definitionJs: string;
  documentJs: string;
  hash: string;
}

export interface ReportHost {
  require: (name: string) => unknown;
}

import type { ReportSubject, SignoffSpec } from '../types';
import type { CheckResult } from './expectations';
import type { Requirement } from '../bindings';
import type { InputSheet } from '../inputs';

const SAFE_SEGMENT = /^[A-Za-z0-9_.-]+$/;

/** A bundle file path the compiler accepts: relative, plain segments, a known extension. */
export function isValidBundlePath(path: string): boolean {
  if (typeof path !== 'string' || path.length === 0 || path.length > 200) return false;
  const segments = path.split('/');
  if (segments.some((s) => s === '' || s === '.' || s === '..' || !SAFE_SEGMENT.test(s))) return false;
  return REPORT_BUNDLE_FILE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

/** Problems with the files themselves, before any compiling: shape, size, the two entries. */
export function validateBundleFiles(files: unknown): ReportBundleDiagnostic[] {
  if (!files || typeof files !== 'object' || Array.isArray(files)) {
    return [{ file: null, line: null, column: null, message: 'files must be an object mapping paths to source text' }];
  }
  const diagnostics: ReportBundleDiagnostic[] = [];
  const entries = Object.entries(files as Record<string, unknown>);
  if (entries.length > REPORT_BUNDLE_MAX_FILES) {
    diagnostics.push({ file: null, line: null, column: null, message: `a bundle may hold at most ${REPORT_BUNDLE_MAX_FILES} files` });
  }
  let total = 0;
  for (const [path, content] of entries) {
    if (!isValidBundlePath(path)) {
      diagnostics.push({ file: path, line: null, column: null, message: `"${path}" is not a valid bundle path: use relative paths of plain segments ending in ${REPORT_BUNDLE_FILE_EXTENSIONS.join(', ')}` });
    }
    if (typeof content !== 'string') {
      diagnostics.push({ file: path, line: null, column: null, message: `"${path}" must be source text` });
    } else {
      total += content.length;
    }
  }
  if (total > REPORT_BUNDLE_MAX_TOTAL_BYTES) {
    diagnostics.push({ file: null, line: null, column: null, message: `a bundle may hold at most ${REPORT_BUNDLE_MAX_TOTAL_BYTES} bytes of source` });
  }
  for (const entry of [REPORT_BUNDLE_DEFINITION_ENTRY, REPORT_BUNDLE_DOCUMENT_ENTRY]) {
    if (!(entry in (files as Record<string, unknown>))) {
      diagnostics.push({ file: entry, line: null, column: null, message: `a bundle needs a ${entry} entry` });
    }
  }
  return diagnostics;
}
